import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { ModalCloseButton } from '../pages/kiosk/ModalCloseButton';

// The fullscreen overlay a widget opens into — the kiosk security map, and the
// home screen's Security / Calendar / Thermostat cards. Owns everything that
// isn't the content: the backdrop, the top bar with its close button, the
// grow-from-the-card open and shrink-back close animation, Escape to close,
// and an optional inactivity auto-close for the wall panel.
//
// Content gets the close function so taps inside it (e.g. the map's empty
// space) can run the same animated close as the X.

interface WidgetOverlayProps {
  onClose: () => void;
  // The rect of whatever opened the overlay. The body grows out of it on open
  // and shrinks back into it on close; null skips that and just fades.
  sourceRect: DOMRect | null;
  // Accessible name for the dialog.
  label: string;
  // Top bar: the header on the left, optional controls centered.
  toolbar?: ReactNode;
  header?: ReactNode;
  // Pinned below the body (the security map's light and event rails).
  footer?: ReactNode;
  // Close after this long without a touch. The kiosk wants it so an overlay
  // left open doesn't sit over the wall panel forever; a phone doesn't.
  autoCloseMs?: number;
  bodyClassName?: string;
  onBodyClick?: (close: () => void) => void;
  children: ReactNode | ((close: () => void) => ReactNode);
}

export function WidgetOverlay({
  onClose,
  sourceRect,
  label,
  toolbar,
  header,
  footer,
  autoCloseMs,
  bodyClassName = 'flex-1 min-h-0 relative',
  onBodyClick,
  children,
}: WidgetOverlayProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Maps the fullscreen body rect onto the source rect — the shared geometry
  // for both the grow-open and shrink-close animations.
  function sourceTransform(body: HTMLDivElement): string | null {
    if (!sourceRect) return null;
    const target = body.getBoundingClientRect();
    const dx = sourceRect.left - target.left;
    const dy = sourceRect.top - target.top;
    const sx = sourceRect.width / target.width;
    const sy = sourceRect.height / target.height;
    return `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
  }

  useLayoutEffect(() => {
    const overlay = overlayRef.current;
    const body = bodyRef.current;
    if (!overlay || !body) return;
    // Backdrop fade-in + FLIP grow: the body starts transformed down to the
    // source rect, then transitions to identity.
    overlay.style.opacity = '0';
    const start = sourceTransform(body);
    if (start) {
      body.style.transformOrigin = 'top left';
      body.style.transform = start;
    }
    // Force a style flush so the start state is COMMITTED before the
    // transition is armed — without this the browser coalesces start and end
    // into one frame and the grow never renders (only the fade showed).
    void body.getBoundingClientRect();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlay.style.transition = 'opacity 250ms ease-out';
        overlay.style.opacity = '1';
        body.style.transition = 'transform 350ms cubic-bezier(0.2, 0, 0.2, 1)';
        body.style.transform = '';
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceRect]);

  // Mirrored close: shrink back into the source rect while the backdrop
  // fades, then unmount. Every close path (X, Escape, autoclose, content)
  // routes here.
  const closingRef = useRef(false);
  function close(): void {
    if (closingRef.current) return;
    closingRef.current = true;
    const overlay = overlayRef.current;
    const body = bodyRef.current;
    if (!overlay || !body) {
      onCloseRef.current();
      return;
    }
    const end = sourceTransform(body);
    if (end) {
      body.style.transformOrigin = 'top left';
      body.style.transition = 'transform 350ms cubic-bezier(0.2, 0, 0.2, 1)';
      body.style.transform = end;
    }
    overlay.style.transition = 'opacity 300ms ease-in';
    overlay.style.opacity = '0';
    window.setTimeout(() => onCloseRef.current(), 370);
  }
  // Timers and listeners reach the latest close through a ref.
  const closeRef = useRef(close);
  closeRef.current = close;

  // Inactivity auto-close: any touch anywhere in the overlay re-arms the
  // timer (pointerdown in capture phase so child stopPropagation doesn't hide
  // activity from it).
  const closeTimerRef = useRef<number | null>(null);
  function armAutoclose(): void {
    if (!autoCloseMs) return;
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => closeRef.current(), autoCloseMs);
  }
  useEffect(() => {
    armAutoclose();
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') closeRef.current();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      // z-60, not z-50: the phone's bottom nav anchor is z-51 (see
      // mobile-nav-anchor in index.css), so a z-50 fullscreen overlay has the
      // nav bar floating over its content. Being above it also means the nav
      // is covered rather than tappable while the overlay is open.
      className="fixed inset-0 z-60 flex flex-col gap-4 bg-canvas/95 backdrop-blur-sm px-6 py-6"
      onPointerDownCapture={armAutoclose}
    >
      {/* One row: header on the left, close on the right, toolbar centered.
          The equal outer columns put the toolbar on the true center whatever
          the header's width; a header too long for its column truncates. */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 sm:gap-3 shrink-0">
        <div className="min-w-0">{header}</div>
        {toolbar && <div>{toolbar}</div>}
        <div className="col-start-3 justify-self-end">
          <ModalCloseButton onClick={close} />
        </div>
      </div>

      <div
        ref={bodyRef}
        className={bodyClassName}
        onClick={onBodyClick ? () => onBodyClick(close) : undefined}
      >
        {typeof children === 'function' ? children(close) : children}
      </div>

      {footer}
    </div>
  );
}
